/**
 * Video Gallery Component
 * Displays a gallery of educational videos about BeSafe services.
 * Videos are embedded from YouTube and switch source according to the active language.
 */

import { i18n, type Language } from '../utils/i18n'

export interface VideoItem {
  title: string
  description: string
  youtubeIds: Partial<Record<Language, string>>
}

export interface VideoGalleryConfig {
  title: string
  videos: VideoItem[]
}

export class VideoGallery {
  private config: VideoGalleryConfig
  public element: HTMLElement

  constructor(config: VideoGalleryConfig) {
    this.config = config
    this.element = this.render()
  }

  /**
   * Resolve the YouTube video id for the active language, with a sensible fallback.
   */
  private resolveYoutubeId(video: VideoItem, lang: Language = i18n.getCurrentLanguage()): string {
    return video.youtubeIds[lang] || video.youtubeIds.es || video.youtubeIds.en || ''
  }

  /**
   * Render the video gallery section
   */
  render(): HTMLElement {
    const section = document.createElement('section')
    section.id = 'video-gallery'
    section.className = 'video-gallery-section section'

    section.innerHTML = `
      <div class="container">
        <h2 class="video-gallery-title text-center" data-i18n="videoGallery.title">
          ${this.config.title}
        </h2>

        <div class="video-grid">
          ${this.config.videos.map((video, index) => {
            const youtubeId = this.resolveYoutubeId(video)
            return `
            <div class="video-card" data-video-index="${index}" data-youtube-id="${youtubeId}">
              <div class="video-thumbnail">
                <img class="video-thumbnail-img" src="https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg" alt="" loading="lazy" />
                <div class="video-overlay"></div>
                <div class="play-button">
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="32" cy="32" r="32" fill="rgba(6, 161, 154, 0.9)"/>
                    <path d="M26 20L44 32L26 44V20Z" fill="white"/>
                  </svg>
                </div>
              </div>
              <div class="video-info">
                <h3 class="video-title" data-i18n="videoGallery.videos.${index}.title">
                  ${video.title}
                </h3>
                <p class="video-description" data-i18n="videoGallery.videos.${index}.description">
                  ${video.description}
                </p>
              </div>
            </div>
          `}).join('')}
        </div>
      </div>

      <!-- Video Modal -->
      <div class="video-modal" id="videoModal">
        <div class="video-modal-overlay"></div>
        <div class="video-modal-content">
          <button class="video-modal-close" aria-label="Cerrar video">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="white" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
          <div class="video-modal-player">
            <iframe
              id="modalVideo"
              src=""
              title="YouTube video player"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerpolicy="strict-origin-when-cross-origin"
              allowfullscreen>
            </iframe>
          </div>
        </div>
      </div>
    `

    return section
  }

  /**
   * Initialize the component after it's added to the DOM
   */
  init(): void {
    this.attachEventListeners()
    this.setupLanguageListener()
  }

  /**
   * Swap thumbnail image and stored youtube id when the language changes.
   */
  private setupLanguageListener(): void {
    window.addEventListener('languageChanged', () => {
      const cards = this.element.querySelectorAll<HTMLElement>('.video-card')
      cards.forEach((card) => {
        const index = parseInt(card.getAttribute('data-video-index') || '0')
        const video = this.config.videos[index]
        if (!video) return
        const youtubeId = this.resolveYoutubeId(video)
        card.setAttribute('data-youtube-id', youtubeId)
        const img = card.querySelector<HTMLImageElement>('.video-thumbnail-img')
        if (img) img.src = `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`
      })
    })
  }

  /**
   * Attach event listeners to video cards and modal
   */
  private attachEventListeners(): void {
    const videoCards = this.element.querySelectorAll('.video-card')
    const modal = this.element.querySelector('#videoModal') as HTMLElement
    const modalIframe = this.element.querySelector('#modalVideo') as HTMLIFrameElement
    const closeButton = this.element.querySelector('.video-modal-close') as HTMLElement
    const overlay = this.element.querySelector('.video-modal-overlay') as HTMLElement

    // Open video modal when clicking on a video card
    videoCards.forEach((card) => {
      card.addEventListener('click', () => {
        const youtubeId = (card as HTMLElement).getAttribute('data-youtube-id') || ''
        if (!youtubeId) return

        modalIframe.src = `https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1`
        modal.classList.add('active')
        document.body.style.overflow = 'hidden'
      })
    })

    // Close modal: clear iframe src to stop playback
    const closeModal = () => {
      modal.classList.remove('active')
      document.body.style.overflow = ''
      modalIframe.src = ''
    }

    closeButton.addEventListener('click', closeModal)
    overlay.addEventListener('click', closeModal)

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('active')) {
        closeModal()
      }
    })
  }
}
